"use client"

import { useState, useEffect } from "react"
import { Search, Check } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { ScrollArea } from "../../../components/ui/scroll-area"
import type { Project, Folder } from "../../../lib/types"
import { updateFolderProjects } from "../../../API"

type Project2 = {
  id: number
  title: string
  thumbnail: string
  description: string
  category: string
  rating?: number
  createdAt?: string
  tags?: string[]
  author?: string
  status?: "active" | "draft" | "archived"
  sport_id: string
  svg_path: string
  id_data: number
  shared: string
  body_state: boolean
  socks_state: boolean
  up_state: boolean
  down_state: boolean
  clone_state: boolean
  images: any
  inside_color: string
  up_state2: boolean
  collar: string
}

interface AssignProjectsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  folder: Folder | null
  projects: Project2[]
  onAssign: (folderId: string, projectIds: number[]) => void
}

export function AssignProjectsDialog({
  open,
  onOpenChange,
  folder,
  projects,
  onAssign,
}: AssignProjectsDialogProps) {
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [search, setSearch] = useState("")

  useEffect(() => {
    if (folder) {
      setSelectedIds([...folder.projectIds])
    }
  }, [folder])

// 1. Filter — guard against null/undefined title
const filteredProjects = projects.filter((p) =>
  (p.title ?? "").toLowerCase().includes(search.toLowerCase())
)

  const toggleProject = (projectId: number) => {
    setSelectedIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    )
  }

  const handleSave = () => {
    if (folder) {
      onAssign(folder.id, selectedIds);
      onOpenChange(false);

      updateFolderProjects(folder.id, JSON.stringify(selectedIds));
    }
  }

  if (!folder) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* ✅ Full screen */}
      <DialogContent className="w-screen max-w-none h-[100dvh] p-0 gap-0 rounded-none">
        {/* Header + Search */}
        <div className="border-b p-6">
          <DialogHeader>
            <DialogTitle>Vincular Projetos</DialogTitle>
            <DialogDescription>
              Selecione os projetos que deseja vincular a pasta &quot;{folder.name}&quot;.
            </DialogDescription>
          </DialogHeader>

          <div className="relative mt-4 max-w-xl">
            <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar projetos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* ✅ Grid */}
        <ScrollArea className="flex-1">
          <div className="p-6 h-[70vh]">
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {filteredProjects.map((project) => {
                const isSelected = selectedIds.includes(project.id)

                return (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => toggleProject(project.id)}
                    className={[
                      "group relative overflow-hidden rounded-xl border text-left transition",
                      "hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-ring",
                      isSelected
                        ? "border-primary/50 ring-1 ring-primary/30"
                        : "border-border",
                    ].join(" ")}
                  >
                    <div className="aspect-[4/3] w-full bg-secondary overflow-hidden">
                      <img
                        src={project.thumbnail}
                        alt=""
                        className="size-full object-cover transition-transform group-hover:scale-[1.03]"
                        crossOrigin="anonymous"
                      />
                    </div>

                    <div className="p-3">
                      <p className="truncate text-sm font-medium text-foreground">
                        {project.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {project.createdAt && (
                          <>Criado em {new Date(project.createdAt).toLocaleDateString("pt-BR")}</>
                        )}
                      </p>
                    </div>

                    {/* badge selecionado */}
                    <div
                      className={[
                        "absolute right-2 top-2 flex items-center gap-1 rounded-full px-2 py-1 text-xs",
                        "backdrop-blur border",
                        isSelected
                          ? "bg-primary/15 border-primary/30 text-foreground"
                          : "bg-background/70 border-border text-muted-foreground opacity-0 group-hover:opacity-100",
                      ].join(" ")}
                    >
                      {isSelected ? (
                        <>
                          <Check className="size-3.5" />
                          Selecionado
                        </>
                      ) : (
                        "Selecionar"
                      )}
                    </div>
                  </button>
                )
              })}
            </div>

            {filteredProjects.length === 0 && (
              <p className="py-16 text-center text-sm text-muted-foreground">
                Nenhum projeto encontrado.
              </p>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <DialogFooter className="border-t p-6">
          <div className="flex w-full items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {selectedIds.length}{" "}
              {selectedIds.length === 1 ? "selecionado" : "selecionados"}
            </p>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave}>Salvar</Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}